import { Injectable } from '@angular/core';
import Dexie from 'dexie';
import { Observable } from 'rxjs';
import { AppDB, IdbParameterDataEntry } from './AppDB';
import { IdbCatalogName, IdbCatalogTypeMap, IdbOtCatalogName, IdbOtCatalogTypeMap } from './idb-catalog-name.enum';
import { CatalogsInterface, CountryInterface, GroupsCatalog, SubGroupCatalog } from '@core/interfaces';
import { UrbanArea, Region } from '@shared/interfaces/certify/urban-area';
import { PlantListInterface } from '../pages/record/interfaces';
import { ICompanyModel } from '@core/models/admin/company/company.model';
import { IUserModel } from '@core/models/shared/user.model';
import { IUserRoles } from '@core/models/admin/roles/roles.model';
import { StripePrice } from '../core/models/admin-manager/stripePrice';
import { CatalogsVehicleInterface } from '../store/catalog-vehicle/catalog-vehicle.models';
import { IdbOtDefaultDataEntry, IdbSvgAssetEntry } from './idb.types';
import { IMenuByUserResponse } from '../store/menu-by-user/menu-by-user.model';
import { IApplication } from '@core/models/admin/application/application.model';
import { IAuthenticatedModel } from '../store/login/login.models';
import { RegionModel } from '../pages/profiler/interfaces/Tenants';
import { IBusinessPlanDetailResponse } from '../store/business-plant-detail/business-plant-detail.models';
import { CatalogSessionService } from '@core/services/common/catalog-session.service';

@Injectable({
  providedIn: 'root',
})
export class ManageIdbService {
  private db!: AppDB;
  private dbReady!: Promise<void>;
  private cleanup?: Promise<void>;

  // Only one constructor, initDb, ensureOpen, clearAll, saveRegions, selectRegions should exist. Removed duplicates.
  async saveRegions(regions: Region[], epoch = this.session.epoch): Promise<void> {
    await this.ensureOpen(epoch);
    this.session.assertCurrent(epoch);
    try {
      await this.db.transaction('rw', this.db.regions, async () => {
        await this.db.regions.clear();

        await this.db.regions.bulkPut(regions);
      });
    } catch (error) {
      console.error('[IDB] Error saving regions', error);
      throw error;
    }
  }

  async selectRegions(): Promise<Region[]> {
    await this.ensureOpen();
    return this.db.regions.toArray();
  }

  async saveAreas(areas: UrbanArea[], epoch = this.session.epoch): Promise<void> {
    await this.ensureOpen(epoch);
    this.session.assertCurrent(epoch);
    try {
      await this.db.transaction('rw', this.db.areas, async () => {
        await this.db.areas.clear();

        await this.db.areas.bulkPut(areas);

        await this.verifyTablesExist();
      });
    } catch (error) {
      console.error('[IDB] Error saving areas', error);
      throw error;
    }
  }

  async selectAreas(): Promise<UrbanArea[]> {
    await this.ensureOpen();
    return this.db.areas.toArray();
  }

  public async verifyTablesExist(): Promise<void> {
    await this.ensureOpen();
    await this.db.areas.count().catch(() => {
      return -1;
    });
    await this.db.regions.count().catch(() => {
      return -1;
    });
  }

  constructor(private session: CatalogSessionService) {
    this.initDB();
  }

  private initDB(): void {
    this.db = new AppDB();
    this.dbReady = this.openDatabase();
  }

  private async openDatabase(): Promise<void> {
    if (!this.db.isOpen()) {
      await this.db.open();
    }
  }

  private async ensureOpen(epoch = this.session.epoch): Promise<void> {
    try {
      this.session.assertCurrent(epoch);
      if (this.session.needsCleanup()) {
        await this.cleanupInvalidCache();
      }
      await this.readyDatabase();
      this.session.assertCurrent(epoch);
    } catch (error) {
      console.error('[IDB] Database access blocked or failed', error);
      throw error;
    }
  }

  private async readyDatabase(): Promise<void> {
    try {
      await this.dbReady;
    } catch (error) {
      console.error('[IDB] Initial open failed; reopening database', error);
      this.dbReady = this.openDatabase();
      await this.dbReady;
    }
    if (!this.db.isOpen()) {
      this.dbReady = this.openDatabase();
      await this.dbReady;
    }
  }

  public async clearAll(): Promise<void> {
    await this.readyDatabase();
    // One all-table transaction queues behind existing writes, not just their
    // Rx subscriptions. No old transaction can commit after this clear.
    await this.db.transaction('rw', this.db.tables, () => this.db.clear());
  }

  public get catalogEpoch(): number {
    return this.session.epoch;
  }

  public get cacheInvalidated(): boolean {
    return this.session.needsCleanup();
  }

  public get catalogCancellation$(): Observable<void> {
    return this.session.cancelled$;
  }

  public prepareCatalogAccess(epoch: number): Promise<void> {
    return this.ensureOpen(epoch);
  }

  public cleanupInvalidCache(): Promise<void> {
    if (!this.cleanup) {
      this.session.resetStores();
      this.cleanup = this.clearWithRetry().then(() => this.session.cleanupSucceeded());
      this.cleanup = this.cleanup.then(
        () => {
          this.cleanup = undefined;
        },
        (error: unknown) => {
          this.cleanup = undefined;
          throw error;
        },
      );
    }
    return this.cleanup;
  }

  private async clearWithRetry(): Promise<void> {
    try {
      await this.clearAll();
    } catch (error) {
      console.error('Catalog cache clear failed; retrying once', error);
      await this.clearAll();
    }
  }

  async saveCatalogs(catalogs: CatalogsInterface, epoch = this.session.epoch): Promise<void> {
    await this.ensureOpen(epoch);
    this.session.assertCurrent(epoch);
    await this.db.transaction(
      'rw',
      this.db.countryCatalog,
      this.db.orderStatusCatalog,
      this.db.orderItemStatusCatalog,
      this.db.plantOperativeModes,
      async () => {
        await Promise.all([
          this.db.countryCatalog.clear(),
          this.db.orderStatusCatalog.clear(),
          this.db.orderItemStatusCatalog.clear(),
          this.db.plantOperativeModes.clear(),
        ]);
        await Promise.all([
          this.db.countryCatalog.bulkPut(catalogs.Countries ?? []),
          this.db.orderStatusCatalog.bulkPut(catalogs.OrderStatuses ?? []),
          this.db.orderItemStatusCatalog.bulkPut(catalogs.OrderItemStatuses ?? []),
          this.db.plantOperativeModes.bulkPut(catalogs.PlantOperativeModes ?? []),
        ]);
      },
    );
  }

  async saveVehicleCatalogs(catalogs: CatalogsVehicleInterface, epoch = this.session.epoch): Promise<void> {
    await this.ensureOpen(epoch);
    this.session.assertCurrent(epoch);
    await this.db.transaction(
      'rw',
      this.db.equipmentCategories,
      this.db.vehicleTypes,
      this.db.units,
      this.db.gpsProviders,
      this.db.vehicleStatus,
      async () => {
        await Promise.all([
          this.db.equipmentCategories.clear(),
          this.db.vehicleTypes.clear(),
          this.db.units.clear(),
          this.db.gpsProviders.clear(),
          this.db.vehicleStatus.clear(),
        ]);
        await Promise.all([
          this.db.equipmentCategories.bulkPut(catalogs.EquipmentCategories ?? []),
          this.db.vehicleTypes.bulkPut(catalogs.VehicleTypes ?? []),
          this.db.units.bulkPut(catalogs.Units ?? []),
          this.db.gpsProviders.bulkPut(catalogs.GPSProviders ?? []),
          this.db.vehicleStatus.bulkPut(catalogs.VehicleStatuses ?? []),
        ]);
      },
    );
  }

  // reconstructs CatalogsInterface from its normalized tables, null if any table is empty (cache miss)
  async selectCatalogsSnapshot(): Promise<CatalogsInterface | null> {
    await this.ensureOpen();
    const [countries, orderStatuses, orderItemStatuses, plantOperativeModes] = await Promise.all([
      this.db.countryCatalog.toArray(),
      this.db.orderStatusCatalog.toArray(),
      this.db.orderItemStatusCatalog.toArray(),
      this.db.plantOperativeModes.toArray(),
    ]);
    if (!countries.length || !orderStatuses.length || !orderItemStatuses.length || !plantOperativeModes.length) {
      return null;
    }
    return {
      Countries: countries,
      OrderStatuses: orderStatuses,
      OrderItemStatuses: orderItemStatuses,
      PlantOperativeModes: plantOperativeModes,
    };
  }

  // reconstructs CatalogsVehicleInterface from its normalized tables; "Plants" isn't persisted by saveVehicleCatalogs
  async selectVehicleCatalogsSnapshot(): Promise<CatalogsVehicleInterface | null> {
    await this.ensureOpen();
    const [equipmentCategories, vehicleTypes, units, gpsProviders, vehicleStatus] = await Promise.all([
      this.db.equipmentCategories.toArray(),
      this.db.vehicleTypes.toArray(),
      this.db.units.toArray(),
      this.db.gpsProviders.toArray(),
      this.db.vehicleStatus.toArray(),
    ]);
    if (
      !equipmentCategories.length ||
      !vehicleTypes.length ||
      !units.length ||
      !gpsProviders.length ||
      !vehicleStatus.length
    ) {
      return null;
    }
    return {
      EquipmentCategories: equipmentCategories,
      VehicleTypes: vehicleTypes,
      Units: units,
      GPSProviders: gpsProviders,
      Plants: [],
      VehicleStatuses: vehicleStatus,
    };
  }

  async savePlants(plants: PlantListInterface[]): Promise<void> {
    await this.ensureOpen();
    await this.db.transaction('rw', this.db.plants, async () => {
      await this.db.plants.clear();
      await this.db.plants.bulkPut(plants.filter((p) => p.IsActive && p.IsBusinessPlanActive && !p.InactiveFlag));
    });
  }

  // unfiltered plants list (includes inactive/pending) for the dashboard configuration table
  async savePlantsFull(plants: PlantListInterface[]): Promise<void> {
    await this.ensureOpen();
    await this.db.transaction('rw', this.db.plantsFull, async () => {
      await this.db.plantsFull.clear();
      await this.db.plantsFull.bulkPut(plants);
    });
  }

  async saveMenus(response: IMenuByUserResponse): Promise<void> {
    await this.ensureOpen();
    await this.db.menus.put({ key: 'menus', response });
  }

  async saveApplications(applications: IApplication[]): Promise<void> {
    await this.ensureOpen();
    await this.db.applications.put({ key: 'applications', applications });
  }

  async saveAuthSession(email: string, response: IAuthenticatedModel, epoch = this.session.epoch): Promise<void> {
    await this.ensureOpen(epoch);
    this.session.assertCurrent(epoch);
    await this.db.authSessions.put({ email, response });
  }

  async selectAuthSession(email: string): Promise<IAuthenticatedModel | null> {
    await this.ensureOpen();
    const entry = await this.db.authSessions.get(email);
    return entry?.response ?? null;
  }

  // invalidates the cached login response so the next login always re-fetches it
  async clearAuthSessions(): Promise<void> {
    await this.ensureOpen();
    await this.db.authSessions.clear();
  }

  async saveCompanyRegions(regions: RegionModel[]): Promise<void> {
    await this.ensureOpen();
    await this.db.companyRegions.put({ key: 'companyRegions', regions });
  }

  async saveBusinessPlantDetail(detail: IBusinessPlanDetailResponse): Promise<void> {
    await this.ensureOpen();
    await this.db.businessPlantDetails.put(detail);
  }

  async selectBusinessPlantDetail(BusinessPlanGuid: string): Promise<IBusinessPlanDetailResponse | null> {
    await this.ensureOpen();
    return (await this.db.businessPlantDetails.get(BusinessPlanGuid)) || null;
  }

  async saveGroupsCatalog(groups: GroupsCatalog[], epoch = this.session.epoch): Promise<void> {
    await this.ensureOpen(epoch);
    this.session.assertCurrent(epoch);
    let subgroups: SubGroupCatalog[] = [];
    groups.forEach(({ SubGroups }) => {
      subgroups = subgroups.concat(...(SubGroups ?? []));
    });
    await this.db.transaction('rw', this.db.groups, this.db.subGroups, async () => {
      await Promise.all([this.db.groups.clear(), this.db.subGroups.clear()]);
      await this.db.subGroups.bulkPut(subgroups);
      await this.db.groups.bulkPut(groups);
    });
  }

  async saveCompanyInfo(companyInfo: ICompanyModel): Promise<void> {
    await this.ensureOpen();
    await this.db.transaction('rw', this.db.companyInfo, async () => {
      await this.db.companyInfo.clear();
      await this.db.companyInfo.add(companyInfo);
    });
  }

  async saveAccountInfo(accountInfo: IUserModel): Promise<void> {
    await this.ensureOpen();
    await this.db.transaction('rw', this.db.accountInfo, async () => {
      await this.db.accountInfo.clear();
      await this.db.accountInfo.add(accountInfo);
    });
  }

  async saveUserRoles(userRoles: IUserRoles[]): Promise<void> {
    await this.ensureOpen();
    await this.db.transaction('rw', this.db.userRoles, async () => {
      await this.db.userRoles.clear();
      await this.db.userRoles.bulkPut(userRoles);
    });
  }

  async pushNewGroup(group: GroupsCatalog): Promise<void> {
    await this.ensureOpen();
    await this.db.groups.put(group);
  }

  async pushNewSubGroup(subGroup: SubGroupCatalog, epoch = this.session.epoch): Promise<void> {
    await this.ensureOpen(epoch);
    this.session.assertCurrent(epoch);
    await this.db.transaction('rw', this.db.groups, this.db.subGroups, async () => {
      const group: GroupsCatalog = await this.db.groups.get(subGroup.GroupId);
      if (group) {
        group.SubGroups.push(subGroup);
        await this.db.groups.update(group.GroupId, group);
      }
      await this.db.subGroups.put(subGroup);
    });
  }

  async saveStripePrices(stripePrices: StripePrice[]): Promise<void> {
    await this.ensureOpen();
    await this.db.transaction('rw', this.db.stripePrices, async () => {
      await this.db.stripePrices.clear();
      await this.db.stripePrices.bulkPut(stripePrices);
    });
  }

  public async select<K extends IdbCatalogName>(catalogName: K): Promise<IdbCatalogTypeMap[K]> {
    await this.ensureOpen();

    switch (catalogName) {
      case IdbCatalogName.Regions:
        return (await this.db.regions.toArray()) as IdbCatalogTypeMap[K];
      case IdbCatalogName.Areas:
        return (await this.db.areas.toArray()) as IdbCatalogTypeMap[K];
      case IdbCatalogName.Country:
        return (await this.db.countryCatalog.toArray()) as IdbCatalogTypeMap[K];
      case IdbCatalogName.OrderStatus:
        return (await this.db.orderStatusCatalog.toArray()) as IdbCatalogTypeMap[K];
      case IdbCatalogName.OrderItemStatus:
        return (await this.db.orderItemStatusCatalog.toArray()) as IdbCatalogTypeMap[K];
      case IdbCatalogName.Plants:
        return (await this.db.plants.toArray()) as IdbCatalogTypeMap[K];
      case IdbCatalogName.PlantsFull:
        return (await this.db.plantsFull.toArray()) as IdbCatalogTypeMap[K];
      case IdbCatalogName.Menus: {
        const menusEntry = await this.db.menus.get('menus');
        return (menusEntry?.response ?? null) as IdbCatalogTypeMap[K];
      }
      case IdbCatalogName.Applications: {
        const applicationsEntry = await this.db.applications.get('applications');
        return (applicationsEntry?.applications ?? null) as IdbCatalogTypeMap[K];
      }
      case IdbCatalogName.CompanyRegions: {
        const companyRegionsEntry = await this.db.companyRegions.get('companyRegions');
        return (companyRegionsEntry?.regions ?? null) as IdbCatalogTypeMap[K];
      }
      case IdbCatalogName.EquipmentCategories:
        return (await this.db.equipmentCategories.toArray()) as IdbCatalogTypeMap[K];
      case IdbCatalogName.VehicleTypes:
        return (await this.db.vehicleTypes.toArray()) as IdbCatalogTypeMap[K];
      case IdbCatalogName.Units:
        return (await this.db.units.toArray()) as IdbCatalogTypeMap[K];
      case IdbCatalogName.GpsProviders:
        return (await this.db.gpsProviders.toArray()) as IdbCatalogTypeMap[K];
      case IdbCatalogName.VehicleStatus:
        return (await this.db.vehicleStatus.toArray()) as IdbCatalogTypeMap[K];
      case IdbCatalogName.Groups:
        return (await this.db.groups.toArray()) as IdbCatalogTypeMap[K];
      case IdbCatalogName.SubGroups:
        return (await this.db.subGroups.toArray()) as IdbCatalogTypeMap[K];
      case IdbCatalogName.OperativeModes:
        return (await this.db.plantOperativeModes.toArray()) as IdbCatalogTypeMap[K];
      case IdbCatalogName.CompanyInfo: {
        const companyInfo = await this.db.companyInfo.toArray();
        return companyInfo[0] as IdbCatalogTypeMap[K];
      }
      case IdbCatalogName.AccountInfo: {
        const accountInfo = (await this.db.accountInfo.toArray()) || [];
        return accountInfo[0] as IdbCatalogTypeMap[K];
      }
      case IdbCatalogName.UserRoles:
        return (await this.db.userRoles.toArray()) as IdbCatalogTypeMap[K];
      case IdbCatalogName.StripePrices:
        return (await this.db.stripePrices.toArray()) as IdbCatalogTypeMap[K];
    }
  }

  public async selectCountryByCountryId(id: number): Promise<CountryInterface> {
    await this.ensureOpen();
    return this.db.countryCatalog.where('CountryId').equals(id).first();
  }

  public async selectPlantById(id: number): Promise<PlantListInterface> {
    await this.ensureOpen();
    return this.db.plants.where('PlantId').equals(id).first();
  }

  public async selectPlantByCode(code: string): Promise<PlantListInterface> {
    await this.ensureOpen();
    return await this.db.plants.filter((pl) => pl.PlantCode === code).first();
  }

  async updatePlantProperties<K extends keyof PlantListInterface>(
    plantCode: string,
    props: K[],
    values: PlantListInterface[K][],
    epoch = this.session.epoch,
  ): Promise<PlantListInterface> {
    await this.ensureOpen(epoch);
    this.session.assertCurrent(epoch);
    return this.db.transaction('rw', this.db.plants, async () => {
      const plant = await this.db.plants.filter((pl) => pl.PlantCode === plantCode).first();
      if (props.length !== values.length) {
        throw new Error('Plant properties and values must have the same length');
      }
      for (let index = 0; index < props.length; index++) {
        const prop = props[index];
        const value = values[index];
        plant[prop] = value;
      }
      await this.db.plants.put(plant);
      return plant;
    });
  }

  public async getPlantsByIds(plantCodes: string[]): Promise<PlantListInterface[]> {
    await this.ensureOpen();
    const plants = await this.db.plants.filter((plant) => plantCodes.includes(plant.PlantCode)).toArray();
    return plants;
  }

  async saveParametersData(entries: IdbParameterDataEntry[]): Promise<void> {
    await this.ensureOpen();
    await this.db.parametersData.bulkPut(entries);
  }

  async getParametersData(keys: string[]): Promise<(IdbParameterDataEntry | undefined)[]> {
    await this.ensureOpen();
    return this.db.parametersData.bulkGet(keys);
  }

  async getSvgAsset(url: string): Promise<IdbSvgAssetEntry | undefined> {
    await this.ensureOpen();
    return this.db.svgAssets.get(url);
  }

  async saveSvgAsset(entry: IdbSvgAssetEntry): Promise<void> {
    await this.ensureOpen();
    await this.db.svgAssets.put(entry);
  }

  // ─── OT Performance: Generic save/select for 10 Order Taking catalogs ───

  async saveOtCatalog<K extends IdbOtCatalogName>(catalogName: K, data: IdbOtCatalogTypeMap[K]): Promise<void> {
    await this.ensureOpen();
    const table = this.getOtTable(catalogName);
    await this.db.transaction('rw', table, async () => {
      await table.clear();
      if (catalogName === IdbOtCatalogName.OrderDefaultData) {
        await table.put(data as IdbOtDefaultDataEntry);
      } else {
        await table.bulkPut(data as unknown[]);
      }
    });
  }

  async selectOtCatalog<K extends IdbOtCatalogName>(catalogName: K): Promise<IdbOtCatalogTypeMap[K] | null> {
    await this.ensureOpen();
    const table = this.getOtTable(catalogName);
    if (catalogName === IdbOtCatalogName.OrderDefaultData) {
      const entry = await table.toArray();
      return (entry[0] ?? null) as IdbOtCatalogTypeMap[K] | null;
    }
    const items = await table.toArray();
    return (items.length ? items : null) as IdbOtCatalogTypeMap[K] | null;
  }

  private getOtTable(catalogName: IdbOtCatalogName): Dexie.Table {
    switch (catalogName) {
      case IdbOtCatalogName.PaymentTerms:
        return this.db.otPaymentTerms;
      case IdbOtCatalogName.SalesChannels:
        return this.db.otSalesChannels;
      case IdbOtCatalogName.OrderItemTypes:
        return this.db.otOrderItemTypes;
      case IdbOtCatalogName.TaxClasses:
        return this.db.otTaxClasses;
      case IdbOtCatalogName.CashPaymentTypes:
        return this.db.otCashPaymentTypes;
      case IdbOtCatalogName.DeliveryMethods:
        return this.db.otDeliveryMethods;
      case IdbOtCatalogName.CountriesReceivers:
        return this.db.otCountriesReceivers;
      case IdbOtCatalogName.OrderStatuses:
        return this.db.otOrderStatuses;
      case IdbOtCatalogName.OrderDefaultData:
        return this.db.otOrderDefaultData;
      case IdbOtCatalogName.AdditionalServices:
        return this.db.otAdditionalServices;
    }
  }
}
